import TaxonomySelector from '../../vocabulary/taxonomies/TaxonomySelector';
import Warning from '../../core/Warning';
import EntitlementWarning from '../../core/EntitlementWarning';
import ProjectSummary from '../../core/project/ProjectSummary';
import Upgrade from '../../core/Upgrade';
import ExpandMore from '@material-ui/icons/ArrowDropDown';
import ExpandLess from '@material-ui/icons/ArrowDropUp';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import MenuSelector from '../../core/MenuSelector';
import OptionsSelector from '../../core/OptionsSelector';
import Paging from '../../core/Paging';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import { CONCEPT_LOAD_OPTIONS, DATATYPES, MAX_DEFINITION_LENGTH } from '../../core/utils/constants';
import { getGraphFromProject, makeCancelable, processHashLocation, sortByProp, sortObjects, checkValidity, basicCheckIRI } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import AddFilterDialog from './AddFilterDialog';
import EditDataDialog from './EditDataDialog';
import SearchFilter from './SearchFilter';
import SearchResults from './SearchResults';
import { HAS_PROPERTY, HAS_PROPERTY_WITH_VALUE, DOES_NOT_HAVE_PROPERTY, DOES_NOT_HAVE_PROPERTY_WITH_VALUE } from './constants';

const FILTER_TYPES = [HAS_PROPERTY, HAS_PROPERTY_WITH_VALUE, DOES_NOT_HAVE_PROPERTY, DOES_NOT_HAVE_PROPERTY_WITH_VALUE];
const MAX_FILTER_LENGTH = 2000;
const WARNING_STATE = {
  isWarning: true,
  warningMessage: "Adding this filter is not possible as it will exceed the maximum permitted URL length."
};

const styles = () => {
  return Object.assign({
    advancedSearch: {
      marginRight: 30,
      padding: 12,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      minHeight: 520,
      minWidth: 720
    },
    searchResults: {
      marginTop: 12
    },
    count: {
      backgroundColor: '#FF6F00',
      borderRadius: '50%',
      color: 'white',
      margin: '0px 0px 0px 6px',
      fontSize: '12px',
      display: 'inline-block',
      width: '20px',
      textAlign: 'center',
      verticalAlign: 'middle',
      height: '16px'
    }
  }, globalStyles);
};

const viewOptions = [
  {
    value: "http://www.w3.org/2004/02/skos/core#prefLabel",
    label: "Preferred Label",
    visible: true,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#altLabel",
    label: "Alternative Labels",
    visible: true,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    label: "Class",
    visible: true,
    format: "class",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "iri",
    label: "IRI",
    visible: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#hiddenLabel",
    alias: "hiddenLabel",
    label: "Hidden Labels",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "notation",
    alias: "notation",
    label: "Notations",
    visible: false,
    format: "datatype"
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#definition",
    alias: "definition",
    label: "Definitions",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#example",
    alias: "example",
    label: "Examples",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#editorialNote",
    alias: "editorialNote",
    label: "Editorial Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#scopeNote",
    alias: "scopeNote",
    label: "Scope Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#historyNote",
    alias: "historyNote",
    label: "History Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#changeNote",
    alias: "changeNote",
    label: "Change Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  }
];

const MAX_RESULTS = 50;

class AdvancedSearchContainer extends Component {

  constructor(props) {
    super(props);
    const subType = GS.getActiveSubscriptionType();
    // If user doesn't have permissions for additional properties then type information will be missing
    const filteredPropsInfo = props.loadInfo ?
      props.loadInfo.filter(li => "type" in li)
      :
      [];
    let additionalOptions = filteredPropsInfo.map(info => {
      let format = "";
      const range = info.range ?
        info.range[0] === "http://www.w3.org/2000/01/rdf-schema#Literal" ?
          DATATYPES.STRING
          :
          info.range[0]
        :
        "";
      if (range === "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString") {
        format = "langString";
      }
      else if (info.type.includes("DatatypeProperty") || info.type.includes("AnnotationProperty")) {
        format = "datatype";
      }
      else {
        format = "relation";
      }
      return {
        graph: info.graph,
        value: info.iri,
        label: info.label ?
          info.label[this.props.defaultLang] ?
            info.label[this.props.defaultLang]
            :
            info.label["und"] ?
              info.label["und"]
              :
              info.id
          :
          info.id,
        visible: false,
        additional: true,
        canFilter: range !== "http://www.w3.org/2008/05/skos-xl#Label" && range !== "http://www.w3.org/2001/XMLSchema#dateTime",
        ...(range) && { range },
        format
      };
    });

    sortByProp(additionalOptions, "label", null, false);

    const viewableOptions = [
      ...viewOptions,
      {
        heading: true,
        label: "Ontology Properties"
      },
      ...additionalOptions];
    const visibleValues = new Array(viewOptions.length + additionalOptions.length + 1).fill(true);

    this.state = {
      fullScreen: false,
      pageSize: MAX_RESULTS,
      addFilter: false,
      filters: [],
      filterProperty: "",
      expanded: true,
      filtersURL: "",
      selectedRows: [],
      allowBulkChange: subType === "Managed" || subType === "Trial" || subType === "Premium",
      columns: null,
      visibleValues,
      isLoading: false,
      viewableOptions,
      isLoadingClasses: true,
      lang: this.props.defaultLang,
      concepts: [],
      loadInfo: [],
      classResources: [],
      warningMessage: "",
      text: "",
      scheme: "All",
      searchID: null,
      page: 1,
      noneFound: false,
      searchCount: 0,
      pageCount: 0,
      firstSearch: true,
      slowSearch: false,
      initialSearch: false,
      isWarning: false,
      isErrored: null
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  onExitWarning = () => this.setState({ isWarning: false });


  loadDataFromGraph(iriData, options = {}) {
    this.cancellable1 = makeCancelable(GS.loadDataFromGraph(iriData, options));
    return this.cancellable1;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
    clearTimeout(this.searchTimeout);
  }

  collectResourceData() {
    // First we load association resources
    const opaLoadData = this.props.data.ontologyProjectAssociation ?
      this.props.data.ontologyProjectAssociation.map(opa => {
        return {
          graph: this.props.graph,
          iri: opa
        };
      })
      :
      [];
    this.loadDataFromGraph(opaLoadData, {})
      .promise
      .then(() => {
        // We get all the association information
        const opaSet = this.props.data.ontologyProjectAssociation ?
          this.props.data.ontologyProjectAssociation.map(opa => GS.data(opa, this.props.graph))
          :
          [];
        let loadInfo = [];
        opaSet.forEach(opa => {
          if (opa.associatedClass) {
            const associatedClasses = opa.associatedClass;
            const associatedGraph = getGraphFromProject(opa.associatedProject);
            associatedClasses.forEach(classIRI => loadInfo.push({
              graph: associatedGraph,
              iri: classIRI
            }));
          }
        });
        this.loadDataFromGraph(loadInfo)
          .promise
          .then(() => {
            if (this.cancellable1 && this.cancellable1.isCancelled()) return;
            this.processResources(loadInfo);
            this.setState({ isLoadingClasses: false, loadInfo },
              () => {
                this.handlePageChange();
              }
            );
          })
          .catch((e) => {
            if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
          });
      })
      .catch((e) => {
        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
      });
  }

  processResources(loadInfo) {
    const classResources = loadInfo
      .filter(info => GS.isLoaded(info.iri, info.graph))
      .map(info => GS.data(info.iri, info.graph));
    sortObjects(classResources, 'label');
    if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ classResources });
  }

  componentDidMount() {
    this.collectResourceData();
  }

  handleSearchTooLong = () => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (!this.state.isLoading) return;
    this.setState({ initialSearch: true, firstSearch: false, slowSearch: true }, () => this.handlePageChange());
  };

  isStillSearching = () => {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(this.handleSearchTooLong, 20000);
  };

  handlePageChange = async () => {
    const noCount = this.state.initialSearch;
    if (this.state.firstSearch) this.isStillSearching();
    this.setState(
      { isLoading: true },
      () => {
        let search = [
          {
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: "http://www.w3.org/2004/02/skos/core#Concept"
          }
        ];
        if (this.state.filters.length > 0) {
          const searchFilters = this.state.filters.map(f => {
            const viewableProp = this.state.viewableOptions.find(v => v.value === f.property);
            let value = f.value;
            // Compute all subclasses for filter classes
            try {
              if (value && viewableProp.value === "http://www.w3.org/1999/02/22-rdf-syntax-ns#type") {
                const classList = Array.isArray(value) ? value : [value];
                const subclassList = classList.map(c => {
                  const classInfo = this.state.loadInfo.find(classInfoItem => classInfoItem.iri === c);
                  if (!classInfo || !classInfo.graph) throw new Error("Invalid class: " + c);
                  return GS.getSubclasses(c, classInfo.graph);
                }).flat();
                const fullClassList = [...new Set([...classList, ...subclassList])];
                value = fullClassList;
              }
            }
            catch (e) {
              if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
            return {
              predicate: f.property,
              ...(f.value) && { value },
              ...(f.filterType === DOES_NOT_HAVE_PROPERTY || f.filterType === DOES_NOT_HAVE_PROPERTY_WITH_VALUE) && { invert: true },
              ...(viewableProp.format === "relation" || viewableProp.format === "langString") && {
                ...(f.value) && {
                  isText: true,
                  isExactMatch: f.isExactMatch
                },
                ...(viewableProp.range && viewableProp.range !== DATATYPES.LANGSTRING) && { datatype: viewableProp.range }
              },
              ...(viewableProp.format === "class" && f.property === "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
              && { exclude: "http://www.w3.org/2004/02/skos/core#Concept" }
            };
          });
          search = search.concat(searchFilters);
        }
        if (this.state.scheme !== "All") {
          search.push({
            predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
            value: this.state.scheme
          });
        }
        const searchID = uuidv4();
        //const isIndexedSearch = searchProps.some(prop => prop.value === this.state.labelType && prop.indexed);
        this.setState({ searchID }, () => {
          this.doSearch({
            search: search,
            graph: [this.props.graph],
            noCount,
            fast: this.state.initialSearch,
            basicSort: true,
            entailment: this.props.skosXL ? ["propertyChain"] : [],
            predicates: ["http://www.w3.org/2004/02/skos/core#prefLabel"],
            page: this.state.page,
            language: this.state.lang,
            pageSize: this.state.pageSize,
            //...(!isIndexedProp) && { searchMode: 'Compliant' },
            sort: ["http://www.w3.org/2004/02/skos/core#prefLabel"]
          }, searchID)
            .promise
            .then(data => {
              // Ignore 'old' 
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.results && data.results.length > 0) {
                const sortedConcepts = sortObjects([...data.results], 'prefLabel', this.state.lang, null, false);
                const conceptIDs = sortedConcepts.map(result => result.id);
                this.loadData(conceptIDs, {})
                  .promise
                  .then(() => this.setState({
                    concepts: conceptIDs,
                    page: this.state.page,
                    pageCount: noCount ? 0 : data.pageCount,
                    searchCount: noCount ? 0 : data.searchCount,
                    isLoading: false
                  }))
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                this.setState({
                  isLoading: false,
                  page: 1,
                  concepts: null,
                  pageCount: 1,
                  searchCount: 0
                });
              }
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        });
      });
  };

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(GS.search(payload, uuid));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  expandFilters = () => this.setState({ expanded: true });

  collapseFilters = () => this.setState({ expanded: false });

  resetFilters = () => {
    const newFilters = [];
    const filtersURL = this.computeFiltersURL(newFilters);
    this.setState({ filters: newFilters }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  static getDerivedStateFromProps(props, state) {

    if (state.isLoadingClasses) return null;

    const paramsMap = processHashLocation(props.location);
    const lang = paramsMap.get("lang");
    const text = decodeURIComponent(paramsMap.get("text"));
    const page = paramsMap.get("page");
    const parsedPage = parseInt(page);
    const scheme = paramsMap.get("scheme");
    const columns = paramsMap.get("columns");
    const filtersURL = paramsMap.get("filters");
    let pageSize = paramsMap.get("pageSize");
    let filters = [];

    try {
      if (scheme !== undefined && scheme !== null && scheme !== "" && scheme !== "All") {
        const isValidURL = basicCheckIRI(scheme);
        if (!isValidURL) throw new Error("Invalid scheme.");
      }
      if (columns !== undefined && columns !== null && columns !== "") {
        const columnValues = columns.split(",");
        const invalidColumn = columnValues.some((cv, index) => (cv !== "" && cv !== 'true' && cv !== 'false')
          || (cv !== "" && state.viewableOptions[index].heading === true));
        const wrongNumberColumns = columnValues.length !== state.viewableOptions.length;
        if (invalidColumn || wrongNumberColumns) throw new Error("Invalid column data.");
      }
      if (pageSize === undefined || pageSize === null || (pageSize !== "50" && pageSize !== "100" && pageSize !== "200")) {
        pageSize = '50';
      }
      if (filtersURL !== undefined && filtersURL !== null && filtersURL !== "") {
        const filtersList = filtersURL.split(";");
        filters = filtersList.map(item => {
          const parts = item.split(",");
          if (parts.length < 4) throw new Error("Not enough parts in filter.");
          // Will throw error if not a URL
          const property = decodeURIComponent(parts[0]);
          const isValidURL = basicCheckIRI(property);
          if (!isValidURL) throw new Error("Invalid property IRI: " + property);
          const isValidProperty = state.viewableOptions.find(v => v.value === property);
          if (!isValidProperty) throw new Error("Invalid property: " + property);
          const filterType = parts[1];
          const isExactMatch = parts[2];
          if (isExactMatch !== "" && isExactMatch !== "true" && isExactMatch !== "false") throw new Error("Invalid value for exact match: " + isExactMatch);
          if (!FILTER_TYPES.includes(filterType)) throw new Error("Invalid filter type: " + filterType);

          const value = parts[3] === "" ?
            []
            :
            parts.slice(3).map(v => {
              if (isValidProperty.format === "relation" || isValidProperty.format === "class") {
                const dv = decodeURIComponent(v);
                const isValidURL = basicCheckIRI(dv);
                if (!isValidURL) throw new Error("Expected IRI for filter value: " + dv);
                return dv;
              }
              else
                return decodeURIComponent(v);
            });

          if (value.length > 0) {
            // We need to check that a user hasn't abused the filter values!
            const vo = state.viewableOptions.find(v => v.value === property);
            value.forEach(v => {
              if (v.length > 100) {
                throw new Error("Too long for " + property);
              }
              if (vo.range) {
                let valid = checkValidity(v, vo.range, [], MAX_DEFINITION_LENGTH);
                if (!valid.isValid) throw new Error("Invalid for datatype: " + property);
              }
            });
          }

          if (filterType !== HAS_PROPERTY && filterType !== DOES_NOT_HAVE_PROPERTY && value.length < 1) {
            throw new Error("Invalid value: " + value);
          }

          return {
            property,
            isExactMatch: isExactMatch === 'true',
            filterType,
            ...(value.length > 0) && { value }
          };
        });
      }
    }
    catch (e) {
      console.log(e.message) // eslint-disable-line
      const message = e.message || "Invalid URL for search. Somebody may have adjusted the ontology associations for this project which can make URLs invalid.";
      return {
        isErrored: {
          message
        }
      };
    }

    if ((lang !== state.lang) || (text !== state.text) || (page !== state.page) ||
      (scheme !== state.scheme) || (columns !== state.columns) || (pageSize !== state.pageSize)) {
      const newState = {
        ...(state.text !== text) && {
          text: (text === "undefined" || text === null || text === "" || text.length > 30) ?
            ""
            :
            text
        },
        ...(state.page !== page) && {
          page: page === undefined || page === null || page === "" || isNaN(parsedPage) || parsedPage < 1 || parsedPage > 100 ?
            1
            :
            parsedPage
        },
        ...(state.lang !== lang) && {
          lang: (lang === undefined || lang === null || lang === "" || !props.projectLang.includes(lang)) ?
            props.defaultLang
            :
            lang
        },
        ...(state.scheme !== scheme) && {
          scheme: scheme === undefined || scheme === null || scheme === "" ||
            (!props.taxonomies.some(tx => tx.id === scheme) && scheme !== "All") ?
            "All"
            :
            scheme
        },
        ...(state.columns !== columns) && {
          columns: columns === undefined || columns === null || columns === "" ?
            ""
            :
            columns
        },
        ...(state.filtersURL !== filtersURL) && {
          filtersURL: filtersURL === undefined || filtersURL === null || filtersURL === "" ?
            ""
            :
            filtersURL,
          filters
        },
        ...(state.pageSize !== Number(pageSize)) && {
          pageSize: Number(pageSize)
        }
      };
      return newState;
    }
    return null;
  }

  computeBaseLinkLength = () => {
    let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/editor#project=' + this.props.project;
    link += "&page=" + this.state.page;
    link += "&text=" + encodeURIComponent(this.state.text);
    link += "&lang=" + this.state.lang;
    link += "&scheme=" + this.state.scheme;
    link += "&columns=" + this.state.columns;
    link += "&pageSize=" + this.state.pageSize;
    return link.length;
  };

  updateSearch = (page, text = null, scheme, lang, columns, pageSize, filtersURL) => {
    this.setState({ initialSearch: false, firstSearch: false });
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    const segment = this.props.editMode ? 'editor' : 'search';
    let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/' + segment + '#project=' + this.props.project;
    link += "&page=" + (page || this.state.page);
    link += "&text=" + (text !== null ? encodeURIComponent(text) : encodeURIComponent(this.state.text));
    link += "&lang=" + (lang || this.state.lang);
    link += "&scheme=" + (scheme || this.state.scheme);
    link += "&columns=" + (columns || this.state.columns);
    link += "&pageSize=" + (pageSize || this.state.pageSize);
    link += "&filters=" + (filtersURL !== undefined && filtersURL !== null ? filtersURL : this.state.filtersURL);
    this.props.history.push(link);
  };

  componentDidUpdate(prevProps, prevState) {
    // Note we don't do a page change for a change in columns
    if (!this.state.isLoadingClasses &&
      ((prevState.lang !== this.state.lang)
        || (prevState.text !== this.state.text)
        || (prevState.page !== this.state.page)
        || (prevState.scheme !== this.state.scheme)
        || (prevState.pageSize !== this.state.pageSize)
        || (prevState.filtersURL !== this.state.filtersURL)
      )) {
      this.handlePageChange();
    }
  }

  handlePagingChange = (page) => this.updateSearch(page);

  handleFilterChange = (text) => {
    if (this.state.slowSearch && text.length < 2) return;
    this.updateSearch(1, text);
  };

  handleSchemeChange = (scheme) => this.updateSearch(1, null, scheme);

  changeLanguage = (lang) => this.updateSearch(1, null, null, lang);

  setColumns = async (columns, value, selected) => {
    if (selected) {
      const viewableProp = this.state.viewableOptions.find(v => v.value === value);
      if (viewableProp.additional && viewableProp.format === "relation") {
        try {
          // We need to load the relationship targets in one go to avoid lots of requests
          const targets = this.state.concepts.map(c => {
            const cData = GS.data(c, this.props.graph, false);
            if (!cData) return [];
            if (value in cData && cData[value] !== undefined) {
              const propValues = Array.isArray(cData[value]) ?
                // Might be in object form
                cData[value].map(item => typeof item === 'object' ? item.id : item)
                :
                [typeof cData[value] === 'object' ? cData[value].id : cData[value]];
              return propValues;
            }
            else return [];
          }).flat();
          const uniqueTargets = [...new Set(targets)];
          await this.loadData(uniqueTargets, CONCEPT_LOAD_OPTIONS).promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
        }
        catch (e) {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
      }
    }
    this.updateSearch(null, null, null, null, columns);
  };

  onPageSizeChange = (pageSize) => this.updateSearch(1, null, null, null, null, pageSize);

  clearSelected = () => this.setState({ selectedRows: [] });

  onSelectAll = (iriList) => {
    let newSelections = [...new Set([...this.state.selectedRows, ...iriList])];
    this.setState({ selectedRows: newSelections });
  };

  handleSelectionChange = (iri, status) => {
    let newSelections = [...this.state.selectedRows];
    if (!newSelections.includes(iri) && status) {
      newSelections.push(iri);
    }
    else {
      const index = newSelections.indexOf(iri);
      if (index > -1) {
        newSelections.splice(index, 1);
      }
    }
    this.setState({ selectedRows: newSelections });
  };

  convertColumns = (columns, viewableOptions) => {
    const columnValues = columns ?
      columns.split(",")
      :
      viewableOptions.map(vo => vo.visible === true ?
        'true'
        :
        vo.visible === false ?
          'false'
          :
          'undefined');
    const columnsAsBooleans = columnValues.map(co => co === 'true' ?
      true
      :
      co === 'false' ?
        false
        :
        undefined);
    return columnsAsBooleans;
  };

  onAddFilter = (col) => {
    this.setState({ addFilter: true, filterProperty: col.value });
  };

  addQuickFilter = (property, text, isExactMatch = false) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    const index = newFilters.findIndex(f => f.property === property);
    if (index > -1) {
      newFilters.splice(index, 1);
    }
    newFilters.push({
      property,
      filterType: HAS_PROPERTY_WITH_VALUE,
      isExactMatch,
      value: [text]
    });
    const filtersURL = this.computeFiltersURL(newFilters);
    const URLlength = this.computeBaseLinkLength();
    if (filtersURL.length + URLlength > MAX_FILTER_LENGTH) {
      this.setState(WARNING_STATE);
      return;
    }
    this.setState({ filters: newFilters, addFilter: false, expanded: true }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  computeFiltersURL = (filters) => {
    const filtersURL = filters.map(f => {
      let url = encodeURIComponent(f.property) + "," + f.filterType + "," + f.isExactMatch.toString() + ",";
      if (f.value) {
        const values = f.value.map(v => encodeURIComponent(v));
        url += values.join(",");
      }
      return url;
    }).join(";");
    return filtersURL;
  };

  addNewFilter = (property, filterType, values, isExactMatch = true) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    newFilters.push({
      property,
      filterType,
      isExactMatch,
      ...(values) && { value: [...values] }
    });
    const filtersURL = this.computeFiltersURL(newFilters);
    const URLlength = this.computeBaseLinkLength();
    if (filtersURL.length + URLlength > MAX_FILTER_LENGTH) {
      this.setState(WARNING_STATE);
      return;
    }
    this.setState({ filters: newFilters, addFilter: false, expanded: true }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  onRemoveFilter = (property) => {
    const filters = this.state.filters;
    let newFilters = cloneDeep(filters);
    const index = newFilters.findIndex(f => f.property === property);
    newFilters.splice(index, 1);
    const filtersURL = this.computeFiltersURL(newFilters);
    this.setState({ filters: newFilters }, () => {
      this.updateSearch(1, null, null, null, null, null, filtersURL);
    });
  };

  exitAddFilter = () => this.setState({ addFilter: false, filterProperty: "" });

  fullScreen = (fullScreen) => this.setState({ fullScreen });

  render() {

    const { isLoading, concepts, isErrored, page, pageCount, viewableOptions, pageSize, warningMessage, isWarning,
      lang, searchCount, isLoadingClasses, classResources, allowBulkChange, addFilter, filterProperty, filters,
      columns, visibleValues, selectedRows, fullScreen, expanded, scheme } = this.state;
    const { defaultLang, graph, editMode, taxonomies, project, classes, entitlements, skosXL, ontologyProjectAssociation } = this.props;

    if (editMode && !allowBulkChange) {
      return <div data-test="no-access" style={{ paddingTop: 60, paddingRight: 30, width: '100%', textAlign: 'center' }}>
        <Typography align="center" variant="body2">Editing is not available with your current subscription.</Typography>
        <Typography align="center" variant="body2">This feature requires a minimum of a Premium subscription.</Typography>
        <Upgrade />
      </div>;
    }

    if (isErrored) return <Errored dataTest="as-error" data={isErrored} />;
    if (isLoadingClasses) return <Loading />;

    const isEntitledToEdit = entitlements.includes(ENTITLEMENTS.EDIT_TAXONOMY);
    if (editMode && !isEntitledToEdit) {
      return <div
        className={classes.advancedSearch}
        data-test="no-access">
        <div data-test="search-filtering" className={classes.filterArea}>
          <Grid container spacing={2} classes={{ "spacing-xs-2": this.props.classes.fullGrid }}>
            <Grid item xs={4}>
              <Typography className={classes.mainHeading} style={{ paddingTop: 0, marginTop: 0 }}>Editor</Typography>
            </Grid>
          </Grid>
        </div>
        <EntitlementWarning requiredEntitlements={[ENTITLEMENTS.EDIT_TAXONOMY]} />
      </div>;
    }

    if (!taxonomies) {
      return <div style={{ paddingRight: 30 }}>
        <ActionText offset="180px">There are no taxonomies in this project.</ActionText>
      </div>;
    }

    const taxonomyList = taxonomies.map(t => t.id);

    const classList = classResources
      .map(classItem => ({
        value: classItem.id,
        graph: classItem.inNamedGraph,
        label: classItem.label && classItem.label[defaultLang] ?
          classItem.label[defaultLang]
          :
          classItem.label["und"] ?
            classItem.label["und"]
            :
            classItem.id
      }))
      .sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return - 1;
        return 1;
      });

    const fromResult = (page - 1) * pageSize + 1;
    const toResult = page * pageSize > searchCount ? searchCount : page * pageSize;
    const columnsAsBooleans = this.convertColumns(columns, viewableOptions);
    const resultsContainerHeight = pageCount > 1 ?
      filters.length > 0 ?
        expanded ?
          'calc(100% - 240px)'
          :
          'calc(100% - 110px)'
        :
        'calc(100% - 50px)'
      :
      filters.length > 0 ?
        expanded ?
          'calc(100% - 178px)'
          :
          'calc(100% - 50px)'
        :
        'calc(100% - 150px)';

    return (
      <div
        className={classes.advancedSearch}
        data-test="search-container"
        style={fullScreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, marginRight: 0, borderRadius: 0, zIndex: 500 } : {}}>
        <div data-test="search-filtering" className={classes.filterArea}>
          <Grid container spacing={2} classes={
            {
              "spacing-xs-2": this.props.classes.fullGrid
            }
          }>
            <Grid item xs={4}>
              {fullScreen &&
                <h5 data-test="page-layout-title" className={classes.layoutTitle}>
                  <ProjectSummary
                    id={project}
                    graph={graph}
                    lang={defaultLang}
                    handleNotAvailable={true} />
                </h5>
              }
              <Typography data-test="main-title" className={classes.mainHeading} style={{ paddingTop: 0, marginTop: fullScreen ? -12 : 0 }}>{
                editMode ?
                  'Editor'
                  :
                  'Advanced Search'
              }</Typography>
            </Grid>
            <Grid item xs={2}>
              <TaxonomySelector
                id={project}
                defaultLang={defaultLang}
                selected={scheme}
                taxonomies={taxonomyList}
                disabled={isLoading}
                graph={graph}
                handleChange={this.handleSchemeChange} />
            </Grid>
            <Grid item xs={2}>
              <ProjectLanguageSelector
                disabled={isLoading}
                key={lang}
                selectedLang={lang}
                includeLabel={true}
                onSelect={this.changeLanguage} />
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right' }}>
              {fullScreen ?
                <Tooltip placement="right" title='Exit full screen'>
                  <IconButton data-test="exit-fullscreen" size="small" onClick={() => this.fullScreen(false)}>
                    <FullscreenExitIcon />
                  </IconButton>
                </Tooltip>
                :
                <Tooltip placement="right" title='Full screen'>
                  <IconButton data-test="fullscreen" size="small" onClick={() => this.fullScreen(true)}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              }
            </Grid>
          </Grid>
        </div>
        <div style={{
          marginBottom: 24, height: filters.length > 0 ?
            pageCount > 1 ?
              'calc(100% - 150px)'
              :
              'calc(100% - 160px)'
            :
            pageCount > 1 ?
              'calc(100% - 150px)'
              :
              '100%'
        }}>
          {
            isLoading || isLoadingClasses ?
              <div style={{ paddingTop: 160 }}>
                <Loading />
              </div>
              :
              <React.Fragment>
                <div className={classes.searchResults} style={{ marginBottom: 12 }}>
                  {filters.length > 0 &&
                    <div data-test="filters-area" className={classes.filterArea} style={{ overflow: 'auto', padding: '12px 12px 0px 12px', marginBottom: 12 }}>
                      <div style={{ display: 'flex' }}>
                        <div style={{ flex: 1 }}>
                          <Typography className={classes.subHeading}>
                            <span>Filters</span>
                            {!expanded &&
                              <span data-test="filter-count" className={classes.count}>{filters.length}</span>
                            }
                          </Typography>
                        </div>
                        <div style={{ flex: 0, marginBottom: 4, transform: 'translateY(-4px)', marginRight: 6 }}>
                          <Tooltip title="Reset filters">
                            <IconButton data-test="reset-filters" onClick={this.resetFilters} size="small">
                              <RotateLeftIcon className={classes.suggestionActionIcon} data-test="collapse" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <div style={{ flex: 0, marginBottom: 4, transform: 'translateY(-4px)' }}>
                          {expanded ?
                            <Tooltip title="Hide filters">
                              <IconButton data-test="hide-filters" onClick={this.collapseFilters} size="small">
                                <ExpandLess className={classes.suggestionActionIcon} data-test="collapse" color="primary" />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Show filters">
                              <IconButton data-test="show-filters" onClick={this.expandFilters} size="small">
                                <ExpandMore className={classes.suggestionActionIcon} data-test="expand" color="primary" />
                              </IconButton>
                            </Tooltip>
                          }
                        </div>
                      </div>
                      {expanded &&
                        <div style={{
                          marginTop: 6,
                          ...(fullScreen) && { height: 126, overflow: 'scroll' }
                        }}>
                          {filters.map((filter, index) => {
                            const vo = viewableOptions.find(v => v.value === filter.property);
                            const format = vo.format;
                            const propertyName = vo.label;
                            return <div
                              data-test={"search-filter-" + index}
                              key={filter.property + "-" + filter.value}
                              style={{
                                float: 'left',
                                marginRight: 12
                              }}>
                              <SearchFilter
                                graph={graph}
                                classList={classList}
                                onRemoveFilter={this.onRemoveFilter}
                                taxonomies={taxonomies}
                                propertyName={propertyName}
                                defaultLang={defaultLang}
                                isExactMatch={filter.isExactMatch}
                                project={project}
                                range={vo.range}
                                format={format}
                                filterType={filter.filterType}
                                property={filter.property}
                                value={filter.value} />
                            </div>;
                          })}
                        </div>
                      }
                    </div>
                  }
                  <div style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', marginRight: 12, width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex' }}>
                        {editMode &&
                          <div style={{ marginLeft: 12, marginTop: 6, marginBottom: 6, marginRight: 12 }}>
                            <ProjectConsumer>
                              {({ ontologyProjectAssociation }) => (
                                <EditDataDialog
                                  disabled={isLoading || selectedRows.length === 0}
                                  classList={classList}
                                  ontologyProjectAssociation={ontologyProjectAssociation}
                                  defaultLang={defaultLang}
                                  graph={graph}
                                  resourceList={selectedRows} />
                              )}
                            </ProjectConsumer>
                          </div>
                        }
                        {selectedRows.length > 0 &&
                          <div style={{
                            display: 'flex',
                            width: 190,
                            border: 'solid 1px #e5e5e5',
                            borderRadius: 4,
                            padding: '6px 6px 4px 6px',
                            marginLeft: 12
                          }}>
                            <div style={{ flex: 1, marginTop: 6 }}>
                              <Typography
                                data-test="selected-results"
                                className={classes.showing}
                                variant="body2">{selectedRows.length} Selected for Editing</Typography>
                              {selectedRows.length === 1000 && <Typography data-test="max-selections" variant="caption">Maximum reached.</Typography>}
                            </div>
                            <div style={{ flex: 0 }}>
                              <Tooltip title="Clear selected">
                                <IconButton data-test="clear-selected"
                                  className={classes.smallIconButton}
                                  size="small"
                                  onClick={this.clearSelected}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <div style={{ flex: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        {searchCount > 0 &&
                          <div style={{ width: 160, textAlign: 'right', marginRight: 24 }}>
                            <Typography
                              data-test="showing-results"
                              variant="body2"
                              className={classes.showing}>Showing {fromResult} to {toResult} of {searchCount}</Typography>
                          </div>
                        }
                        {columnsAsBooleans && concepts && concepts.length > 0 &&
                          <div style={{ marginRight: 24, transform: 'translateY(7px)' }}>
                            <OptionsSelector
                              options={viewableOptions}
                              visibleValues={visibleValues}
                              values={columnsAsBooleans}
                              onSelect={this.setColumns} />
                          </div>
                        }
                        {concepts && concepts.length > 0 &&
                          <div style={{ width: 120, marginRight: 12 }}>
                            <MenuSelector
                              dataTest="page-size-selector"
                              label="Results to Show"
                              handleChange={this.onPageSizeChange}
                              selected={pageSize.toString()}
                              hintWord="results size"
                              options={[
                                {
                                  value: 50,
                                  label: "50"
                                },
                                {
                                  value: 100,
                                  label: "100"
                                },
                                {
                                  value: 200,
                                  label: "200"
                                }
                              ]} />
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  data-test="results-container"
                  data-height={resultsContainerHeight}
                  style={{
                    overflow: 'scroll',
                    ...(fullScreen) && { height: resultsContainerHeight }
                  }}>
                  <SearchResults
                    editMode={editMode}
                    graph={graph}
                    classSelector={classList && classList.length > 0 ?
                      <MenuSelector
                        dataTest="class-selector"
                        selected={""}
                        hintWord="Class"
                        handleChange={(value) => this.addQuickFilter("http://www.w3.org/1999/02/22-rdf-syntax-ns#type", value)}
                        options={[
                          ...classList
                        ]} />
                      :
                      null
                    }
                    selectedRows={selectedRows}
                    project={project}
                    allowFilters={filters.length < 5}
                    addFilter={this.onAddFilter}
                    onAddQuickFilter={this.addQuickFilter}
                    ontologyProjectAssociation={ontologyProjectAssociation}
                    excludeFilterProperties={filters.map(f => f.property)}
                    columns={columnsAsBooleans}
                    visibleValues={visibleValues}
                    viewOptions={viewableOptions}
                    onSelectAll={this.onSelectAll}
                    handleSelectionChange={this.handleSelectionChange}
                    concepts={concepts}
                    taxonomies={taxonomies || []}
                    defaultLang={defaultLang}
                    viewLang={lang} />
                </div>
              </React.Fragment>
          }
        </div>
        {!isLoading && <Paging page={page} pageCount={pageCount} handlePageChange={this.handlePagingChange} />}
        {addFilter &&
          <AddFilterDialog
            graph={graph}
            project={project}
            taxonomies={taxonomies || []}
            exclude={filters.filter(f => f.property === filterProperty).map(f => f.value)}
            defaultLang={defaultLang}
            ontologyProjectAssociation={ontologyProjectAssociation}
            skosXL={skosXL}
            classList={classList}
            data={viewableOptions.find(v => v.value === filterProperty)}
            onExitAddFilter={this.exitAddFilter}
            onAddFilter={this.addNewFilter}
          />
        }
        {isWarning && <Warning message={warningMessage} onExitWarning={this.onExitWarning} />}
      </div>
    );
  }

}

AdvancedSearchContainer.defaultProps = {
  loadInfo: [],
  ontologyProjectAssociation: [],
  editMode: false
};

AdvancedSearchContainer.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  editMode: PropTypes.bool,
  projectLang: PropTypes.array.isRequired,
  ontologyProjectAssociation: PropTypes.array,
  entitlements: PropTypes.array.isRequired,
  skosXL: PropTypes.bool.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  taxonomies: PropTypes.array,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withStyles(styles)(AdvancedSearchContainer));